import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Alert, Badge } from 'react-bootstrap';
import { FaUserPlus, FaTrash, FaSearch, FaUserCog, FaKey, FaUserCheck, FaUserTimes, FaUsers, FaSync, FaTimes, FaInfoCircle } from 'react-icons/fa';
import Navigation from '../components/Navigation';
import Spinner from '../components/Spinner';
import * as api from '../services/api';
import './UserAdmin.css';

const UserAdmin = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    given_name: '',
    family_name: '',
    phone_number: '',
    temporary_password: '',
    send_invitation: true
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user =>
        user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.attributes?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.status?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showModal) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showModal]);

  /**
   * Extracts the most meaningful error message from an API response.
   *
   * The backend wraps responses in layers:
   *   response.body (string) -> { success, error, code }   on failure
   *   response.body (string) -> { success, data }          on success
   *
   * extractFromNestedResponse in api.js merges body fields to the top level,
   * so by the time we see the response here it should already be flat —
   * but we defensively handle every nesting level just in case.
   */
  const extractErrorMessage = (response) => {
    if (!response) return 'An unknown error occurred.';

    // Already flat (api.js merged body fields to root)
    if (response.error && typeof response.error === 'string') {
      return response.error;
    }

    // error is an object { message, code }
    if (response.error && typeof response.error === 'object') {
      return response.error.message || JSON.stringify(response.error);
    }

    // Body is still a raw JSON string (shouldn't happen but handle anyway)
    if (response.body && typeof response.body === 'string') {
      try {
        const parsed = JSON.parse(response.body);
        if (parsed.error) {
          return typeof parsed.error === 'string'
            ? parsed.error
            : parsed.error.message || JSON.stringify(parsed.error);
        }
      } catch (_) {
        return response.body;
      }
    }

    // Body is already an object
    if (response.body && typeof response.body === 'object' && response.body.error) {
      const err = response.body.error;
      return typeof err === 'string' ? err : err.message || JSON.stringify(err);
    }

    return 'An unexpected error occurred. Please try again.';
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.listCognitoUsers({ limit: 60 });

      if (response && response.success && response.data) {
        const usersData = response.data.users || [];
        setUsers(usersData);
        setFilteredUsers(usersData);
      } else {
        throw new Error(extractErrorMessage(response) || 'Failed to fetch users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message || 'Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    setModalMode('add');
    setFormData({
      username: '',
      email: '',
      given_name: '',
      family_name: '',
      phone_number: '',
      temporary_password: '',
      send_invitation: true
    });
    setFormErrors({});
    setError(null);
    setSelectedUser(null);
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setModalMode('edit');
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.attributes?.email || '',
      given_name: user.attributes?.given_name || '',
      family_name: user.attributes?.family_name || '',
      phone_number: user.attributes?.phone_number || '',
      temporary_password: '',
      send_invitation: false
    });
    setFormErrors({});
    setError(null);
    setShowModal(true);
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      const response = await api.deleteCognitoUser(username);

      if (response && response.success) {
        setSuccessMessage(`User "${username}" has been deleted successfully.`);
        await fetchUsers();
      } else {
        throw new Error(extractErrorMessage(response) || 'Failed to delete user');
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.message || 'Failed to delete user. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (username) => {
    if (!window.confirm(`Send password reset email to user "${username}"?`)) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      const response = await api.resetCognitoUserPassword(username);

      if (response && response.success) {
        setSuccessMessage(`Password reset email sent to "${username}".`);
      } else {
        throw new Error(extractErrorMessage(response) || 'Failed to reset password');
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleUserStatus = async (user) => {
    const action = user.enabled ? 'disable' : 'enable';

    if (!window.confirm(`Are you sure you want to ${action} user "${user.username}"?`)) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      const response = user.enabled
        ? await api.disableCognitoUser(user.username)
        : await api.enableCognitoUser(user.username);

      if (response && response.success) {
        setSuccessMessage(`User "${user.username}" has been ${action}d successfully.`);
        await fetchUsers();
      } else {
        throw new Error(extractErrorMessage(response) || `Failed to ${action} user`);
      }
    } catch (err) {
      console.error(`Error ${action}ing user:`, err);
      setError(err.message || `Failed to ${action} user. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManageGroups = async (user) => {
    alert(`User: ${user.username}\nGroups: ${user.groups?.join(', ') || 'None'}`);
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const updated = { ...prev, [name]: newValue };

      // Auto-populate username from email when the username field has not
      // been manually customised (still empty or still mirrors the email)
      if (name === 'email' && modalMode === 'add') {
        const usernameIsEmpty = !prev.username.trim();
        const usernameStillMirrorsEmail = prev.username === prev.email;
        if (usernameIsEmpty || usernameStillMirrorsEmail) {
          updated.username = newValue;
        }
      }

      return updated;
    });

    // Clear field-level error when the user corrects input
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
    // Also clear username error when email auto-fills it
    if (name === 'email' && formErrors.username) {
      setFormErrors(prev => ({ ...prev, username: null }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (modalMode === 'add') {
      if (!formData.username.trim()) {
        errors.username = 'Username is required';
      }

      if (!formData.email.trim()) {
        errors.email = 'Email address is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (modalMode === 'add') {
        const response = await api.createCognitoUser({
          username: formData.username,
          email: formData.email,
          given_name: formData.given_name,
          family_name: formData.family_name,
          phone_number: formData.phone_number,
          temporary_password: formData.temporary_password,
          send_invitation: formData.send_invitation
        });

        console.log('Create user response:', response);

        if (response && response.success) {
          setSuccessMessage(`User "${formData.username}" created successfully.`);
          setShowModal(false);
          await fetchUsers();
        } else {
          // Surface the backend error message directly inside the modal
          setError(extractErrorMessage(response));
        }
      } else {
        setError('Editing user attributes is not yet supported through this interface.');
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      let message = err.message || 'Failed to save user. Please try again.';
      try {
        const parsed = JSON.parse(message);
        if (parsed.error) {
          message = typeof parsed.error === 'string'
            ? parsed.error
            : parsed.error.message || message;
        }
      } catch (_) {
        // message was already a plain string
      }
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormErrors({});
    setError(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleString();
    } catch (e) {
      return dateString;
    }
  };

  const getUserGroups = (user) => user.groups || [];

  if (loading) {
    return (
      <>
        <Navigation />
        <Container className="text-center my-5">
          <Spinner />
          <p className="mt-2">Loading user data...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <Container className="user-admin-container">
        <div className="page-header">
          <h1 className="page-title">
            <FaUserCog className="me-2" />
            User Administration
          </h1>
          <p className="page-description">
            Manage Cognito users, roles, and permissions for the Agent Enterprise platform.
          </p>
        </div>

        {/* Page-level alerts — only shown when the modal is closed */}
        {!showModal && error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        <Card className="mb-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="search-box">
                <FaSearch className="search-icon" />
                <Form.Control
                  type="text"
                  placeholder="Search users by username, email, or status..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-primary"
                  onClick={fetchUsers}
                  disabled={isSubmitting}
                >
                  <FaSync className="me-2" /> Refresh
                </Button>
                <Button variant="primary" onClick={handleAddUser}>
                  <FaUserPlus className="me-2" /> Add User
                </Button>
              </div>
            </div>

            <div className="table-responsive">
              <Table hover className="user-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Enabled</th>
                    <th>Groups</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        No users found matching your search criteria.
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map(user => (
                      <tr key={user.username}>
                        <td>{user.username}</td>
                        <td>{user.attributes?.email || 'N/A'}</td>
                        <td>
                          <span className={`status-badge status-${user.status?.toLowerCase()}`}>
                            {user.status}
                          </span>
                        </td>
                        <td>
                          {user.enabled ? (
                            <Badge bg="success">Enabled</Badge>
                          ) : (
                            <Badge bg="danger">Disabled</Badge>
                          )}
                        </td>
                        <td>
                          {getUserGroups(user).length > 0 ? (
                            getUserGroups(user).map(group => (
                              <Badge key={group} bg="info" className="me-1">
                                {group}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-muted">None</span>
                          )}
                        </td>
                        <td>{formatDate(user.created_at)}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              title="Reset Password"
                              onClick={() => handleResetPassword(user.username)}
                              disabled={isSubmitting}
                            >
                              <FaKey />
                            </Button>
                            <Button
                              variant={user.enabled ? "outline-warning" : "outline-success"}
                              size="sm"
                              title={user.enabled ? "Disable User" : "Enable User"}
                              onClick={() => handleToggleUserStatus(user)}
                              disabled={isSubmitting}
                            >
                              {user.enabled ? <FaUserTimes /> : <FaUserCheck />}
                            </Button>
                            <Button
                              variant="outline-info"
                              size="sm"
                              title="Manage Groups"
                              onClick={() => handleManageGroups(user)}
                              disabled={isSubmitting}
                            >
                              <FaUsers />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              title="Delete User"
                              onClick={() => handleDeleteUser(user.username)}
                              disabled={isSubmitting}
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </Container>

      {/* Custom Modal — rendered outside Container to avoid blur / stacking issues */}
      {showModal && (
        <div className="user-modal-overlay" onClick={handleCloseModal}>
          <div className="user-modal-container" onClick={(e) => e.stopPropagation()}>

            {/* ── Header ── */}
            <div className="user-modal-header">
              <h2 className="user-modal-title">
                <FaUserPlus className="me-2" />
                {modalMode === 'add' ? 'Create New User' : 'Edit User'}
              </h2>
              <button className="modal-close-btn" onClick={handleCloseModal}>
                <FaTimes size={24} />
              </button>
            </div>

            {/* ── Body ── */}
            <div className="user-modal-body">

              {/* Modal-level error — stays visible while the modal is open */}
              {error && (
                <Alert
                  variant="danger"
                  dismissible
                  onClose={() => setError(null)}
                  className="mb-3"
                >
                  <strong>Error: </strong>{error}
                </Alert>
              )}

              <Form noValidate>

                {/* ── Section: Account Identity ── */}
                <div className="form-section-label">Account Identity</div>

                {/* Email — always shown first; drives the username auto-fill */}
                <Form.Group className="mb-3">
                  <Form.Label>
                    Email Address <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="user@example.com"
                    isInvalid={!!formErrors.email}
                    required
                    disabled={modalMode === 'edit'}
                    className="modal-input"
                  />
                  <Form.Text className="text-muted">
                    Used for login and all Cognito notifications.
                    {modalMode === 'edit' && ' Email cannot be changed after account creation.'}
                  </Form.Text>
                  <Form.Control.Feedback type="invalid">
                    {formErrors.email}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Username — shown for add mode; auto-filled from email */}
                {modalMode === 'add' && (
                  <Form.Group className="mb-4">
                    <Form.Label>
                      Username <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      placeholder="Auto-filled from email, or enter a custom username"
                      isInvalid={!!formErrors.username}
                      required
                      className="modal-input"
                    />
                    <Form.Text className="text-muted">
                      Auto-filled from the email address above. Override only if you
                      need a different login identifier (e.g. a short handle). Cannot
                      be changed after the account is created.
                    </Form.Text>
                    <Form.Control.Feedback type="invalid">
                      {formErrors.username}
                    </Form.Control.Feedback>
                  </Form.Group>
                )}

                {/* ── Section: Personal Information ── */}
                <div className="form-section-label">Personal Information</div>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>First Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="given_name"
                        value={formData.given_name}
                        onChange={handleInputChange}
                        placeholder="Jane"
                        className="modal-input"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="family_name"
                        value={formData.family_name}
                        onChange={handleInputChange}
                        placeholder="Doe"
                        className="modal-input"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-4">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    placeholder="+12065551234"
                    className="modal-input"
                  />
                  <Form.Text className="text-muted">
                    Optional. Must be in E.164 format — country code followed by the
                    number with no spaces or dashes (e.g. <code>+12065551234</code>).
                  </Form.Text>
                </Form.Group>

                {/* ── Section: Password & Invitation (add mode only) ── */}
                {modalMode === 'add' && (
                  <>
                    <div className="form-section-label">Password &amp; Invitation</div>

                    <Form.Group className="mb-2">
                      <Form.Label>Temporary Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="temporary_password"
                        value={formData.temporary_password}
                        onChange={handleInputChange}
                        placeholder="Leave blank to auto-generate"
                        className="modal-input"
                      />
                      <Form.Text className="text-muted">
                        Leave blank to let Cognito generate a secure temporary password
                        automatically. If you provide one, it must satisfy the rules below.
                      </Form.Text>
                    </Form.Group>

                    {/* Cognito password policy hint */}
                    <Alert variant="info" className="password-rules-alert mb-3">
                      <div className="password-rules-header">
                        <FaInfoCircle className="me-2" />
                        <strong>Cognito Password Requirements</strong>
                      </div>
                      <ul className="password-rules-list mb-0">
                        <li>Minimum <strong>8 characters</strong></li>
                        <li>At least one <strong>uppercase</strong> letter (A–Z)</li>
                        <li>At least one <strong>lowercase</strong> letter (a–z)</li>
                        <li>At least one <strong>number</strong> (0–9)</li>
                        <li>At least one <strong>special character</strong> — e.g. <code>! @ # $ % ^ &amp; *</code></li>
                      </ul>
                    </Alert>

                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        id="send_invitation"
                        name="send_invitation"
                        label="Send invitation email to user"
                        checked={formData.send_invitation}
                        onChange={handleInputChange}
                        className="modal-checkbox"
                      />
                      <Form.Text className="text-muted ms-4">
                        When checked, Cognito will email the user their temporary
                        password and a sign-in link. Uncheck only if you intend to
                        share credentials out-of-band.
                      </Form.Text>
                    </Form.Group>
                  </>
                )}

              </Form>
            </div>

            {/* ── Footer ── */}
            <div className="user-modal-footer">
              <Button
                variant="secondary"
                onClick={handleCloseModal}
                disabled={isSubmitting}
                className="modal-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="modal-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {modalMode === 'add' ? 'Creating...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    <FaUserPlus className="me-2" />
                    {modalMode === 'add' ? 'Create User' : 'Save Changes'}
                  </>
                )}
              </Button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default UserAdmin;
